/**
 * SENDING THE QUOTE, FROM WHEREVER SOMEBODY IS STANDING.
 *
 * Anthony, 2026-09-09: "send quote is really far down and hard to find, I
 * hope this goes in service advisor wizard."
 *
 * It was a closure inside jobPage. That is why it was only ever reachable
 * from one place -- three quarters of the way down the job card, under the
 * lines, past the money band. The single most important button the desk
 * presses all day, and you had to scroll to it.
 *
 * So it lives here now, taking the job rather than capturing it, and both
 * the job card and the advisor's walk call the same one. Not two
 * implementations that will drift: the ten-minute double-send guard, the
 * thirty day clock, the pre-purchase wording and Anthony's phrasing are
 * decided once.
 */

import { db, api, customerSite } from "./client.ts";
import { ensurePortalToken, updateJobField, shopTrade, getJob, listLines, type JobRow, type LineItem } from "./jobs.ts";
import { isPrePurchase, inSoCal } from "../domain/prepurchase.ts";
import { carPhrase } from "../domain/carphrase.ts";
import { money } from "../ui/dom.ts";
import { isVisitOnly, quotesAVisit, visitOfferText } from "../domain/visitquote.ts";
import { shopName } from "../ui/words.ts";
import { firstNameOf } from "../domain/greetname.ts";

export type SendResult = { ok: true } | { ok: false; error: string };

/**
 * Whether a quote has just gone to this customer. Broken out because the
 * caller decides how to ask -- the job card uses confirm(), and a wizard
 * step on a phone should not.
 */
export async function quoteWentRecently(jobId: string): Promise<boolean> {
  try {
    const sb = await db();
    const { data } = await sb.from("comm_events")
      .select("occurred_at").eq("job_id", jobId).eq("direction", "outbound")
      .gte("occurred_at", new Date(Date.now() - 10 * 60_000).toISOString())
      /* The visit offer is a quote too, in other words. */
      .or("body.ilike.%the quote for%,body.ilike.%for a free estimate%").limit(1);
    return (data ?? []).length > 0;
  } catch {
    /* A check that cannot run must not stop a send. */
    return false;
  }
}

/** Which road a quote goes down. Text unless somebody says otherwise. */
export type SendVia = "sms" | "email";

/**
 * Whether this customer could be emailed a quote at all.
 *
 * Asked by the screens so they can offer the button rather than offering
 * it and failing -- a button that is there and does not work is worse than
 * one that is not there, because the advisor has already told the customer
 * it is coming.
 */
export function canEmailQuote(job: JobRow): boolean {
  return Boolean((job.customers?.email ?? "").trim());
}

/** Whether this customer could be texted one. */
export function canTextQuote(job: JobRow): boolean {
  return Boolean((job.customers?.phone ?? "").trim());
}

/**
 * THE SEND ITSELF.
 *
 * `total` is passed rather than summed here because the job card has
 * already worked out the figure with the discount on it, and a second sum
 * in a second place is a second answer.
 *
 * The double-send guard is the CALLER's, via `askAgain`. Jonathan got the
 * price for his Audi twice, ten seconds apart -- the button disables on
 * click, so one page cannot do it, but the page's copy of the job is a
 * snapshot and two tabs both saw a quote that had never been sent.
 * Re-sending later is legitimate, so it asks rather than refuses.
 *
 * ─── THE OTHER ROAD ────────────────────────────────────────────────────
 *
 * Anthony, 2026-09-16: "Add email option for sending a quote." `via` picks
 * it. Text stays the default and the wording of this function's SMS body
 * is untouched -- the email is a DOCUMENT and is built on the server in
 * desk/quote-email.ts, because the lines, the shop's registration and the
 * expiry all belong in it and none of them belong in a text.
 *
 * The guard above runs for both roads on purpose. A customer who was
 * texted the price four minutes ago and is then emailed the same price has
 * been contacted twice about one number, and it reads as a shop that does
 * not know what it has already said.
 */
export async function sendTheQuote(
  job: JobRow,
  lines: LineItem[],
  total: number,
  askAgain?: () => boolean | Promise<boolean>,
  via: SendVia = "sms",
): Promise<SendResult> {
  if (!job.customers) return { ok: false, error: "This job has no customer on it." };

  if (askAgain && await quoteWentRecently(job.id)) {
    if (!(await askAgain())) return { ok: false, error: "" };
  }

  /* The emailed quote is a priced document with a total and an expiry
     on it. A free estimate visit has neither, and a $0.00 quote in
     somebody's inbox reads as a mistake, so that one only goes by text. */
  if (via === "email" && isVisitOnly(lines) && quotesAVisit(await shopTrade())) {
    return { ok: false, error: "A free estimate visit goes by text. Add a mobile number on Details." };
  }
  if (via === "email") return emailTheQuote(job);

  /**
   * THE FIGURE IS READ AT THE MOMENT IT GOES, not when the card was drawn.
   * Jinx's alternator, 2026-10-09: lines were added on the open card, the
   * card's `total` was still the $150.00 it had when it opened, and the
   * text said $150.00 while the link said $704.75. The lines save as they
   * are typed, so the database is right and the page's copy is the stale
   * one. A read that fails falls back to what the caller passed rather
   * than stopping the send.
   */
  try {
    const [fresh, freshLines] = await Promise.all([getJob(job.id), listLines(job.id)]);
    if (fresh) total = fresh.totals.total;
    lines = freshLines;
  } catch { /* keep the caller's figure */ }

  const tok = await ensurePortalToken(job.customers.id);
  if (!tok) return { ok: false, error: "Could not make a link for this customer." };

  /**
   * A pre-purchase inspection needs no approval to be sent -- no quote
   * does -- but it does need the customer told WHEN. Read from whatever is
   * written on the job, so it catches a lead that came in saying "pre
   * purchase" as well as a line an advisor typed as "PPI".
   */
  const ppi = isPrePurchase(job.title, job.complaint,
    ...lines.map((l) => l.description));

  /* Not a bare split. A customer saved off caller id is "WIRELESS CALLER"
     or a town, and quotes, chases and booking texts greeted customers
     "Hi WIRELESS" 116 times in the thirty days to 2026-10-06. */
  const first = firstNameOf(job.customers.full_name) || "there";
  const link = `${customerSite()}/garage.html?t=${encodeURIComponent(tok)}`;
  /* Cached for the session, so this is a lookup only on the first quote
     of the day. It decides whether a customer with no vehicle on file
     reads "your car" or "your door". */
  const trade = await shopTrade();

  /**
   * A FREE ESTIMATE VISIT IS OFFERED, NOT PRICED. Anthony, 2026-09-29: a
   * garage door shop's quote is "the customer saying yes to a free visit
   * for an estimate". "Here's the quote for your door: $0.00" is the
   * sentence that would otherwise go, so it gets the same words the
   * automatic sender uses (src/domain/visitquote.ts), with the link on the
   * end for anybody who would rather tap than type.
   */
  const visit = quotesAVisit(trade) && isVisitOnly(lines);

  try {
    const sb = await db();
    const { data: { session } } = await sb.auth.getSession();
    const res = await fetch(api("/api/sms/send"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify({
        customer_id: job.customers.id,
        job_id: job.id,
        /* A template: the server signs it with the shop's name and closes
           it with the opt-out line (src/domain/smsbrand.ts). The visit
           offer already names the shop and is left unsigned. */
        template: true,
        // Anthony's voice: the number, then the link, then nothing.
        message: visit
          ? `${visitOfferText(first === "there" ? "" : first, shopName(), shopName(), "", trade).trim()} `
            + `Or say yes here: ${link}`
          : `Hi ${first}, here's the quote for ${
          carPhrase(job.vehicles, { prePurchase: ppi, trade })}: ${money(total)}. `
          + `You can see what's on it and approve it here: ${link}`,
      }),
    });
    const out = await res.json() as { ok?: boolean; error?: string };
    if (!res.ok || !out.ok) return { ok: false, error: out.error ?? "That did not go." };

    /* A price with no end date is a price forever. Thirty days, set when it
       goes rather than when it was written, because the clock starts when
       the customer can first read it. */
    if (!job.quote_expires_at) {
      const until = new Date(Date.now() + 30 * 864e5).toISOString();
      await updateJobField(job.id, "quote_expires_at", until);
      job.quote_expires_at = until;
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * THE QUOTE AS AN EMAIL.
 *
 * Almost nothing happens here on purpose. The text road builds its own
 * sentence in the browser because a text IS one sentence; an emailed quote
 * is a document with the shop's registration, every line, the total and an
 * expiry on it, and building that here would mean the browser holding a
 * second copy of rules that already live on the server. So this hands over
 * a job id and the server does the rest -- see server/desk/quote-email.ts
 * for the document and server/routes/quote-send-email.ts for who is
 * allowed to send it.
 *
 * `quote_expires_at` is stamped by the server on this road rather than
 * here, so the page's copy of the job is read back afterwards. Without
 * that the card would keep saying the quote has never been sent until
 * somebody reloaded, which is exactly how a customer ends up with two.
 */
export async function emailTheQuote(job: JobRow): Promise<SendResult> {
  if (!job.customers) return { ok: false, error: "This job has no customer on it." };
  if (!canEmailQuote(job)) {
    return { ok: false, error: "No email on file for them. Add one on the Details tab." };
  }

  try {
    const sb = await db();
    const { data: { session } } = await sb.auth.getSession();
    const res = await fetch(api("/api/quote/send-email"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ job_id: job.id }),
    });
    const out = await res.json() as { ok?: boolean; error?: string; warning?: string };
    if (!res.ok || !out.ok) return { ok: false, error: out.error ?? "That did not go." };

    /* The server stamped it. Catch the page's copy up rather than working
       out what it probably wrote -- a guess here and a write there is two
       answers to "when does this price stop standing". */
    if (!job.quote_expires_at) {
      const { data } = await sb.from("jobs")
        .select("quote_expires_at").eq("id", job.id).maybeSingle();
      const until = (data as { quote_expires_at: string | null } | null)?.quote_expires_at;
      if (until) job.quote_expires_at = until;
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Kept for the pre-purchase note the job card shows beside the button. */
export { isPrePurchase, inSoCal };
